import React, { createRef, forwardRef, useEffect } from 'react';
import styled, { css } from 'styled-components';
import {
  addColorSet,
  gray30,
  IconSearch,
  VisuallyHidden,
  screenSmMin,
  screenXsMax,
  spacer24,
} from '@spotify-internal/encore-web';

import { useViewport, Viewport } from 'libs/utils/useViewport';
import { CloseButton, Input, SearchButtonIcon, SearchFormInputIcon } from 'components/SidePanel';
import { useSidePanel, setSearchValue, showSearch, hideSearch } from './SidePanelState';
import { selectTeam } from '.';
import { useT } from '@spotify-internal/i18n-core';

const InputWrapper = styled.div`
  line-height: 0;
  position: relative;
`;

const SearchInputContainer = styled.div<{ showSearch: boolean }>`
  @media (min-width: ${screenSmMin}) {
    ${({ showSearch }) =>
      showSearch &&
      css`
        left: ${spacer24};
        right: ${spacer24};
        position: absolute;
      `}
  }

  @media (max-width: ${screenXsMax}) {
    position: relative;
    width: 100vw;
    padding: 14px ${spacer24} 0;
    margin-top: 20px;
    margin-bottom: -6px;
    margin-left: -${spacer24};
    margin-right: -${spacer24};
    border-top: 1px solid ${gray30};
  }
`;

export const TeamSearch = forwardRef<HTMLElement, {}>(function NavigationSearch(
  _props,
  firstFocusableRef,
) {
  const t = useT();
  const [{ searchValue, shouldShowSearch, searchResults }, sidePanelDispatch] = useSidePanel();
  const searchTeamsInputRef = createRef<HTMLInputElement>();
  const viewport = useViewport();
  const isMobile = viewport === Viewport.XS;

  useEffect(() => {
    if (shouldShowSearch) {
      searchTeamsInputRef.current?.focus();
    }
  }, [searchTeamsInputRef, shouldShowSearch]);

  const searchTitle = t(
    'TITLE_TEAMSEARCH_0',
    'Filter teams',
    'Title for search bar to filter teams',
  );
  const closeButtonTitle = t(
    'BUTTON_TEAMSEARCH_0',
    'Close teams filter view',
    'Text for button to close the view of the teams filter',
  );

  return (
    <SearchInputContainer showSearch={shouldShowSearch}>
      <InputWrapper className={addColorSet('invertedDark')}>
        {!shouldShowSearch && !isMobile && (
          <SearchButtonIcon
            ref={firstFocusableRef}
            title={searchTitle}
            onClick={() => sidePanelDispatch(showSearch())}
          >
            <IconSearch iconSize={16} />
          </SearchButtonIcon>
        )}

        {(shouldShowSearch || isMobile) && (
          <>
            <VisuallyHidden as="label" htmlFor="sidepanel-search-teams">
              {t(
                'L_TEAMSEARCH_0',
                'Filter teams, press Enter to select first result',
                'Label for teams filter',
              )}
            </VisuallyHidden>
            <SearchFormInputIcon iconLeading={<IconSearch iconSize={16} />}>
              <Input
                placeholder={searchTitle}
                type="text"
                value={searchValue}
                onChange={(e: React.ChangeEvent) =>
                  sidePanelDispatch(setSearchValue((e.target as HTMLInputElement).value))
                }
                onFocus={() => sidePanelDispatch(showSearch())}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (searchResults.length > 0 && e.code === 'Enter') {
                    selectTeam(searchResults[0].organizationUri);
                  }
                }}
                ref={searchTeamsInputRef}
                id="sidepanel-search-teams"
                areSearchResultsVisible={shouldShowSearch}
              />
            </SearchFormInputIcon>
            <CloseButton
              disabled={!shouldShowSearch}
              buttonTitle={closeButtonTitle}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                sidePanelDispatch(hideSearch());
              }}
            />
          </>
        )}
      </InputWrapper>
    </SearchInputContainer>
  );
});
